import { NUMBER_LIMIT_BOUNDARY } from "@/features/hoverIdle/core/numberLimitBoundary";
import { compareHuge, type HugeNumber } from "@/shared/numbers";
import {
  calculateNHovBranchExperienceGain,
  calculateNHovBranchLevel,
} from "./nHovGrowthLogic";
import {
  calculateNHovHgCostDivisorHuge,
  NHOV_HG_DISCOUNT_OUTER_REEXECUTION_BOUNDARY,
} from "./hgDiscountLogic";
import type { NHovBalance } from "@/features/hoverIdle/balance/hovResetBalanceTypes";

export interface ResolveHgDiscountBoundaryReexecutionStateOptions {
  readonly runBaseHov: HugeNumber;
  readonly balance?: NHovBalance;
}

export interface HgDiscountBoundaryReexecutionState {
  readonly currentDivisor: HugeNumber;
  readonly freshExperienceGain: HugeNumber;
  readonly freshIntegerLevel: number;
  readonly freshIntegerLevelDivisor: HugeNumber;
  readonly outerThreshold: HugeNumber;
  readonly canExecute: boolean;
  readonly nextPreviewDivisor: HugeNumber;
  readonly executionDivisor: HugeNumber;
  readonly shouldClearBoundaryMetadata: boolean;
  readonly postExecutionHgDiscountExperience: HugeNumber;
}

// While the boundary cap remains, the saved hgDiscount experience is treated
// as already compressed into `NUMBER_LIMIT_BOUNDARY`; only this reset section's
// fresh gain may clear the outer boundary.
export function resolveHgDiscountBoundaryReexecutionState(
  options: ResolveHgDiscountBoundaryReexecutionStateOptions,
): HgDiscountBoundaryReexecutionState {
  const freshExperienceGain = calculateNHovBranchExperienceGain({
    runBaseHov: options.runBaseHov,
    balance: options.balance,
  });
  return resolveHgDiscountBoundaryReexecutionStateFromExperienceGain(
    freshExperienceGain,
    options.balance,
  );
}

export function resolveHgDiscountBoundaryReexecutionStateFromExperienceGain(
  freshExperienceGain: HugeNumber,
  balance?: NHovBalance,
): HgDiscountBoundaryReexecutionState {
  const freshIntegerLevel = calculateNHovBranchLevel({
    experience: freshExperienceGain,
  });
  const freshIntegerLevelDivisor = calculateNHovHgCostDivisorHuge(
    freshIntegerLevel,
    balance?.branches.hgDiscount,
  );
  const canExecute =
    compareHuge(
      freshIntegerLevelDivisor,
      NHOV_HG_DISCOUNT_OUTER_REEXECUTION_BOUNDARY,
    ) >= 0;
  const resolvedDivisor = canExecute
    ? freshIntegerLevelDivisor
    : NUMBER_LIMIT_BOUNDARY;

  return {
    currentDivisor: NUMBER_LIMIT_BOUNDARY,
    freshExperienceGain,
    freshIntegerLevel,
    freshIntegerLevelDivisor,
    outerThreshold: NHOV_HG_DISCOUNT_OUTER_REEXECUTION_BOUNDARY,
    canExecute,
    nextPreviewDivisor: resolvedDivisor,
    executionDivisor: resolvedDivisor,
    shouldClearBoundaryMetadata: canExecute,
    postExecutionHgDiscountExperience: freshExperienceGain,
  };
}
